import { useCallback, useRef } from 'react';

import {
  canSubmitAiBuilderQuestionSet,
  getActiveQuestionAnswer,
} from '../store/aiBuilderQuestionSetState';
import { createAiBuilderClientRequestId } from '../store/aiBuilderTurnRequests';
import {
  type AiBuilderChatState,
  type AiBuilderPendingQuestionSet,
} from '../store/types';
import { isAiBuilderAuthExpiredError } from '../utils/aiBuilderAuthExpiry';
import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';
import {
  cancelInteraction as cancelServerInteraction,
  type CancelServerAiBuilderChatInteractionRequest,
} from '../utils/serverAiBuilderChatTransport';
import {
  type AiBuilderInteractionAnswer,
  type AiBuilderTurnRequest,
} from '../utils/types';

type UpdateAiBuilderChatState = (
  updater: (previous: AiBuilderChatState) => AiBuilderChatState,
) => void;

type FrozenQuestionSetSubmission = {
  request: AiBuilderTurnRequest;
  userMessageId: string;
};

export type UseAiBuilderSubmitQuestionSetParams = {
  clientCopy: AiBuilderChatCopy;
  createTurnContext: () => AiBuilderTurnRequest['context'];
  draftInstanceId: string;
  getChatState: () => AiBuilderChatState;
  getNextRequestId: () => number;
  onAuthExpired: () => void;
  submitInteraction: (
    request: AiBuilderTurnRequest,
    userMessageId: string,
  ) => Promise<void>;
  updateState: UpdateAiBuilderChatState;
  cancelInteraction?: (
    request: CancelServerAiBuilderChatInteractionRequest,
  ) => Promise<unknown>;
  createClientRequestId?: () => string;
};

export const useAiBuilderSubmitQuestionSet = ({
  cancelInteraction = cancelServerInteraction,
  clientCopy,
  createClientRequestId = createAiBuilderClientRequestId,
  createTurnContext,
  draftInstanceId,
  getChatState,
  getNextRequestId,
  onAuthExpired,
  submitInteraction,
  updateState,
}: UseAiBuilderSubmitQuestionSetParams) => {
  const submissionInFlightRef = useRef(false);
  const cancellationInFlightRef = useRef(false);
  const failedSubmissionRef = useRef<FrozenQuestionSetSubmission | null>(null);

  const runSubmission = useCallback(
    async (submission: FrozenQuestionSetSubmission): Promise<boolean> => {
      if (submissionInFlightRef.current || cancellationInFlightRef.current) {
        return false;
      }

      submissionInFlightRef.current = true;
      setSubmissionPending(updateState, submission);
      try {
        await submitInteraction(submission.request, submission.userMessageId);
        if (isCurrentQuestionSetSubmission(getChatState(), submission)) {
          completeQuestionSet(updateState, submission);
          failedSubmissionRef.current = null;
        }
        return true;
      } catch {
        if (isCurrentQuestionSetSubmission(getChatState(), submission)) {
          failQuestionSet(updateState, submission);
          failedSubmissionRef.current = submission;
        }
        return false;
      } finally {
        submissionInFlightRef.current = false;
      }
    },
    [getChatState, submitInteraction, updateState],
  );

  const submitQuestionSet = useCallback(async (): Promise<boolean> => {
    if (submissionInFlightRef.current || cancellationInFlightRef.current) {
      return false;
    }

    const state = getChatState();
    const pending = state.pendingQuestionSet;
    if (!pending || !canSubmitAiBuilderQuestionSet(pending)) return false;

    const answers = snapshotAnswers(pending);
    if (!answers) return false;

    const requestId = getNextRequestId();
    const clientRequestId = createClientRequestId();
    const userMessageId = `user-question-set-${requestId}`;
    const context = createTurnContext();
    const request: AiBuilderTurnRequest = {
      requestId,
      clientRequestId,
      clientCopy,
      intent: {
        type: 'interaction_response',
        interaction_id: pending.interactionId,
        answers,
      },
      context,
    };
    Object.freeze(request.intent);
    const submission = { request, userMessageId };
    updateState(previous => ({
      ...previous,
      draft:
        previous.pendingQuestionSet?.composerDraftSnapshot ?? previous.draft,
      pendingQuestionSet: null,
      turnInProgress: true,
      messages: [
        ...previous.messages,
        {
          id: userMessageId,
          kind: 'user',
          content: createQuestionAnswerProjection(pending, answers),
          status: 'sending',
          retry: { clientRequestId, type: 'questionSet' },
        },
      ],
    }));

    return runSubmission(submission);
  }, [
    createClientRequestId,
    clientCopy,
    createTurnContext,
    getChatState,
    getNextRequestId,
    runSubmission,
    updateState,
  ]);

  const retryQuestionSet = useCallback(async (): Promise<boolean> => {
    const failedSubmission = failedSubmissionRef.current;
    if (!failedSubmission) return false;

    return runSubmission(failedSubmission);
  }, [runSubmission]);

  const cancelQuestionSet = useCallback(async (): Promise<boolean> => {
    if (cancellationInFlightRef.current || submissionInFlightRef.current) {
      return false;
    }

    const state = getChatState();
    const pending = state.pendingQuestionSet;
    if (!pending || !state.sessionId) return false;

    cancellationInFlightRef.current = true;
    let cancellationValidated = false;
    try {
      await cancelInteraction({
        sessionId: state.sessionId,
        draftInstanceId,
        interactionId: pending.interactionId,
        onCancelled: () => {
          cancellationValidated = true;
        },
      });
      if (!cancellationValidated) return false;

      restoreComposerAndClearPending(updateState, pending.interactionId);
      failedSubmissionRef.current = null;
      return true;
    } catch (error: unknown) {
      if (isAiBuilderAuthExpiredError(error)) {
        onAuthExpired();
        return false;
      }
      setQuestionSetError(updateState, pending.interactionId, 'cancel_failed');
      return false;
    } finally {
      cancellationInFlightRef.current = false;
    }
  }, [
    cancelInteraction,
    draftInstanceId,
    getChatState,
    onAuthExpired,
    updateState,
  ]);

  return { submitQuestionSet, retryQuestionSet, cancelQuestionSet };
};

const snapshotAnswers = (
  pending: AiBuilderPendingQuestionSet,
): AiBuilderInteractionAnswer[] | null => {
  if (!pending.interactionId.trim()) return null;

  const answers: AiBuilderInteractionAnswer[] = [];
  for (const question of pending.questions) {
    const answer = getActiveQuestionAnswer(pending, question.questionId);
    if (!answer) return null;

    if (answer.mode === 'text') {
      if (!question.allowDirectInput || !answer.text.trim()) return null;
      answers.push({ question_id: question.questionId, text: answer.text });
      continue;
    }

    const optionIds = [
      ...new Set(
        answer.optionIds.map(optionId => optionId.trim()).filter(Boolean),
      ),
    ];
    const expectedCount = question.selectionMode === 'single' ? 1 : null;
    const knownOptionIds = new Set(
      question.options.map(option => option.optionId),
    );
    if (
      optionIds.length === 0 ||
      (expectedCount !== null && optionIds.length !== expectedCount) ||
      optionIds.some(optionId => !knownOptionIds.has(optionId))
    ) {
      return null;
    }
    if (answer.mode === 'mixed') {
      if (!question.allowDirectInput || !answer.text.trim()) return null;
      answers.push({
        question_id: question.questionId,
        selected_option_ids: [...optionIds],
        text: answer.text,
      });
    } else {
      answers.push({
        question_id: question.questionId,
        selected_option_ids: [...optionIds],
      });
    }
  }

  answers.forEach(answer => {
    if (answer.selected_option_ids) Object.freeze(answer.selected_option_ids);
    Object.freeze(answer);
  });
  Object.freeze(answers);
  return answers;
};

const createQuestionAnswerProjection = (
  pending: AiBuilderPendingQuestionSet,
  answers: readonly AiBuilderInteractionAnswer[],
): string =>
  pending.questions
    .map((question, index) => {
      const answer = answers[index];
      if (!answer) throw new Error('Question set projection must be complete.');

      const selectedLabels = answer.selected_option_ids
        ?.map(
          optionId =>
            question.options.find(option => option.optionId === optionId)
              ?.label ?? optionId,
        )
        .join(', ');
      const answerText = [selectedLabels, answer.text]
        .filter((value): value is string => Boolean(value))
        .join(', ');
      return `${question.question}\n${answerText}`;
    })
    .join('\n\n');

const failQuestionSet = (
  updateState: UpdateAiBuilderChatState,
  submission: FrozenQuestionSetSubmission,
) => {
  updateState(previous =>
    isCurrentQuestionSetSubmission(previous, submission)
      ? {
          ...previous,
          turnInProgress: false,
          messages: updateUserMessageStatus(
            previous.messages,
            submission.userMessageId,
            'failed',
          ),
        }
      : previous,
  );
};

const setSubmissionPending = (
  updateState: UpdateAiBuilderChatState,
  submission: FrozenQuestionSetSubmission,
) => {
  updateState(previous =>
    isCurrentQuestionSetSubmission(previous, submission)
      ? {
          ...previous,
          turnInProgress: true,
          pendingQuestionSet: previous.pendingQuestionSet
            ? { ...previous.pendingQuestionSet, submissionError: null }
            : previous.pendingQuestionSet,
          messages: updateUserMessageStatus(
            previous.messages,
            submission.userMessageId,
            'sending',
          ),
        }
      : previous,
  );
};

const setQuestionSetError = (
  updateState: UpdateAiBuilderChatState,
  interactionId: string,
  error: string,
) => {
  updateState(previous =>
    previous.pendingQuestionSet?.interactionId === interactionId
      ? {
          ...previous,
          pendingQuestionSet: {
            ...previous.pendingQuestionSet,
            submissionError: error,
          },
        }
      : previous,
  );
};

const restoreComposerAndClearPending = (
  updateState: UpdateAiBuilderChatState,
  interactionId: string,
) => {
  updateState(previous => {
    if (previous.pendingQuestionSet?.interactionId !== interactionId) {
      return previous;
    }

    return {
      ...previous,
      draft: previous.pendingQuestionSet.composerDraftSnapshot,
      pendingQuestionSet: null,
    };
  });
};

const completeQuestionSet = (
  updateState: UpdateAiBuilderChatState,
  submission: FrozenQuestionSetSubmission,
) => {
  updateState(previous =>
    isCurrentQuestionSetSubmission(previous, submission)
      ? {
          ...previous,
          turnInProgress: false,
          messages: updateUserMessageStatus(
            previous.messages,
            submission.userMessageId,
            'sent',
          ),
        }
      : previous,
  );
};

const isCurrentQuestionSetSubmission = (
  state: AiBuilderChatState,
  submission: FrozenQuestionSetSubmission,
) =>
  state.messages.some(
    message =>
      message.kind === 'user' &&
      message.id === submission.userMessageId &&
      message.retry?.type === 'questionSet' &&
      message.retry.clientRequestId === submission.request.clientRequestId,
  );

const updateUserMessageStatus = (
  messages: AiBuilderChatState['messages'],
  userMessageId: string,
  status: 'sending' | 'sent' | 'failed',
) =>
  messages.map(message =>
    message.kind === 'user' && message.id === userMessageId
      ? { ...message, status }
      : message,
  );
